import React, {
    forwardRef,
    useRef,
    useImperativeHandle,
    useCallback,
} from 'react';
import { Form } from 'react-bootstrap';
import { Controller } from 'react-hook-form';
import { get, noop } from 'lodash';
import PropTypes from 'prop-types';
import { useNodeContext } from 'contexts/NodeProvider';
import ErrorMessage from 'components/Common/ErrorMessage';
import './Input.scss';

const TextArea = forwardRef(
    (
        {
            label,
            name,
            value,
            onKeyDown,
            onKeyUp,
            disabled,
            errorMessage,
            onChange,
            maxLength,
            normalize,
            required,
            ...rest
        },
        ref
    ) => {
        return (
            <Form.Group className="text-field">
                {label && (
                    <Form.Label
                        className="text-field-label"
                        id={`label-inputquestion-${label}`}
                        htmlFor={name}
                    >
                        {label}
                        <span className="text-danger">
                            {required ? ' *' : ' '}
                        </span>
                    </Form.Label>
                )}
                <Form.Control
                    ref={ref}
                    data-testid={name}
                    type="input"
                    as="textarea"
                    rows={3}
                    name={name}
                    onChange={(e) => {
                        const val = normalize(e.target.value, value);
                        onChange(val);
                    }}
                    id={name}
                    value={value}
                    onKeyDown={onKeyDown}
                    onKeyUp={onKeyUp}
                    maxLength={maxLength}
                    aria-labelledby={`label-inputquestion-${label}`}
                    disabled={disabled}
                    {...rest}
                />
                <ErrorMessage message={errorMessage} />
            </Form.Group>
        );
    }
);

const TextAreaWithController = forwardRef(
    (
        {
            control,
            name,
            defaultValue = '',
            label,
            type,
            errors,
            onKeyUp,
            normalize,
            disabled,
            required,
            value,
            onChange = noop,
            registerRef = true,
            ...rest
        },
        ref
    ) => {
        const _ref = useRef();
        const { nodesRef } = useNodeContext();

        useImperativeHandle(ref, () => _ref.current);

        return (
            <Controller
                control={control}
                name={name}
                defaultValue={defaultValue}
                render={({
                    onChange: handleChange,
                    value: formValue,
                    ref: rhfRef,
                }) => {
                    const onRefChange = useCallback((node) => {
                        if (registerRef) {
                            _ref.current = node;
                            rhfRef = _ref;
                            if (nodesRef) {
                                nodesRef.current[name] = node;
                            }
                        }
                    }, []);
                    return (
                        <div className="text-field-container">
                            <TextArea
                                ref={onRefChange}
                                name={name}
                                value={value ?? formValue ?? ''}
                                type={type}
                                onChange={(val) => {
                                    onChange(val);
                                    handleChange(val);
                                }}
                                normalize={normalize}
                                label={label}
                                option={{ label: name }}
                                onKeyUp={onKeyUp}
                                disabled={disabled}
                                errorMessage={get(errors, `${name}`?.message)}
                                required={required}
                                {...rest}
                            />
                        </div>
                    );
                }}
            />
        );
    }
);
TextAreaWithController.propTypes = {
    control: PropTypes.any,
    name: PropTypes.string,
    defaultValue: PropTypes.string,
    label: PropTypes.string,
    type: PropTypes.string,
    errors: PropTypes.shape({
        message: PropTypes.string,
    }),
    onKeyUp: PropTypes.func,
    normalize: PropTypes.func,
    disabled: PropTypes.bool,
    required: PropTypes.bool,
};
TextAreaWithController.defaultProps = {
    normalize: (val) => val,
};
export default TextAreaWithController;
